import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Plus, X } from 'react-feather';
import Avatar from './Avatar';
import { PRIORIDADES, PRIORIDAD_KEYS } from '../../features/tickets';

// Convierte el value de un <select> a id numerico o null (cadena vacia).
const aId = (value) => (value === '' ? null : Number(value));

const TicketProps = ({
  ticket,
  usuarios,
  estados,
  grupos,
  temas,
  etiquetasCatalogo,
  onChange,
  onAddEtiqueta,
  onRemoveEtiqueta,
}) => {
  const [creando, setCreando] = useState(false);
  const [valor, setValor] = useState('');
  const inputRef = useRef(null);
  const etiquetas = ticket.etiquetas || [];
  const listId = `tickets-etiquetas-${ticket.id ?? 'nuevo'}`;

  // Etiquetas del catalogo que el ticket aun no tiene (sugerencias del datalist).
  const disponibles = etiquetasCatalogo.filter(
    (e) => !etiquetas.some((t) => String(t.id) === String(e.id)),
  );

  const confirmarEtiqueta = () => {
    const nueva = valor.trim();
    if (!nueva) {
      setCreando(false);
      setValor('');
      return;
    }
    const yaEnTicket = etiquetas.some((t) => t.nombre.toLowerCase() === nueva.toLowerCase());
    if (!yaEnTicket) {
      // Si coincide con una etiqueta del catalogo, la enlazamos por id;
      // si no, se crea nueva por nombre.
      const existente = etiquetasCatalogo.find(
        (e) => e.nombre.toLowerCase() === nueva.toLowerCase(),
      );
      onAddEtiqueta(existente ? { etiquetaId: existente.id } : { nombre: nueva });
    }
    setValor('');
    if (inputRef.current) inputRef.current.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirmarEtiqueta();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setCreando(false);
      setValor('');
    }
  };

  return (
    <aside className="tickets-props" aria-label="Propiedades del ticket">
      <div className="tickets-props__card">
        <p className="tickets-props__caption">Solicitante</p>
        <div className="tickets-props__solicitante">
          <Avatar nombre={ticket.solicitante_nombre || ticket.solicitante_email} size="md" />
          <div className="tickets-props__solicitante-datos">
            <span className="tickets-props__nombre">
              {ticket.solicitante_nombre || ticket.solicitante_email}
            </span>
            {/* Email bajo el nombre; si no hay nombre, la linea de arriba ya es
                el email y no se repite. */}
            {ticket.solicitante_nombre && ticket.solicitante_email && (
              <span className="tickets-props__email" title={ticket.solicitante_email}>
                {ticket.solicitante_email}
              </span>
            )}
          </div>
        </div>
      </div>

      <label className="tickets-props__field">
        <span className="tickets-props__label">Grupo / bandeja</span>
        <select
          className="tickets-input"
          value={ticket.grupo_id ?? ''}
          onChange={(e) => onChange('grupo_id', aId(e.target.value))}
        >
          {grupos.map((g) => (
            <option key={g.id} value={g.id}>
              {g.nombre}
            </option>
          ))}
        </select>
      </label>

      <label className="tickets-props__field">
        <span className="tickets-props__label">Asignado a</span>
        <select
          className="tickets-input"
          value={ticket.agente ?? ''}
          onChange={(e) => onChange('agente', aId(e.target.value))}
        >
          <option value="">Sin asignar</option>
          {usuarios.map((u) => (
            <option key={u.id} value={u.id}>
              {u.nombre}
            </option>
          ))}
        </select>
      </label>

      <label className="tickets-props__field">
        <span className="tickets-props__label">Estado</span>
        <select
          className="tickets-input"
          value={ticket.estado ?? ''}
          onChange={(e) => onChange('estado', aId(e.target.value))}
        >
          {estados.map((est) => (
            <option key={est.id} value={est.id}>
              {est.descripcion}
            </option>
          ))}
        </select>
      </label>

      <label className="tickets-props__field">
        <span className="tickets-props__label">Prioridad</span>
        <select
          className="tickets-input"
          value={ticket.prioridad}
          onChange={(e) => onChange('prioridad', e.target.value)}
        >
          {PRIORIDAD_KEYS.map((p) => (
            <option key={p} value={p}>
              {PRIORIDADES[p].label}
            </option>
          ))}
        </select>
      </label>

      <label className="tickets-props__field">
        <span className="tickets-props__label">Tema</span>
        <select
          className="tickets-input"
          value={ticket.tema_id ?? ''}
          onChange={(e) => onChange('tema_id', aId(e.target.value))}
        >
          <option value="">Sin tema</option>
          {temas.map((tm) => (
            <option key={tm.id} value={tm.id}>
              {tm.nombre}
            </option>
          ))}
        </select>
      </label>

      <div className="tickets-props__field">
        <span className="tickets-props__label">Etiquetas</span>
        <div className="tickets-tags">
          {etiquetas.map((tag) => (
            <span key={tag.id} className="tickets-tag">
              {tag.nombre}
              <button
                type="button"
                className="tickets-tag__remove"
                aria-label={`Quitar etiqueta ${tag.nombre}`}
                onClick={() => onRemoveEtiqueta(tag.id)}
              >
                <X size={11} aria-hidden="true" />
              </button>
            </span>
          ))}
          {creando ? (
            <>
              <input
                ref={inputRef}
                type="text"
                className="tickets-tag-input"
                list={listId}
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                value={valor}
                maxLength={30}
                placeholder="Etiqueta…"
                aria-label="Nueva etiqueta"
                onChange={(e) => setValor(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={confirmarEtiqueta}
              />
              <datalist id={listId}>
                {disponibles.map((e) => (
                  <option key={e.id} value={e.nombre} />
                ))}
              </datalist>
            </>
          ) : (
            <button
              type="button"
              className="tickets-tag tickets-tag--add"
              aria-label="Añadir etiqueta"
              onClick={() => {
                setCreando(true);
                setValor('');
              }}
            >
              <Plus size={13} aria-hidden="true" />
            </button>
          )}
        </div>
        {disponibles.length > 0 && (
          <div className="tickets-etiquetas-sugeridas">
            <span className="tickets-etiquetas-sugeridas__label">Existentes:</span>
            {disponibles.map((e) => (
              <button
                key={e.id}
                type="button"
                className="tickets-tag tickets-tag--sugerida"
                onClick={() => onAddEtiqueta({ etiquetaId: e.id })}
              >
                <Plus size={11} aria-hidden="true" />
                {e.nombre}
              </button>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
};

TicketProps.propTypes = {
  ticket: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    solicitante_email: PropTypes.string,
    solicitante_nombre: PropTypes.string,
    grupo_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    agente: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    estado: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    prioridad: PropTypes.string,
    tema_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    etiquetas: PropTypes.arrayOf(PropTypes.object),
  }).isRequired,
  usuarios: PropTypes.arrayOf(PropTypes.object),
  estados: PropTypes.arrayOf(PropTypes.object),
  grupos: PropTypes.arrayOf(PropTypes.object),
  temas: PropTypes.arrayOf(PropTypes.object),
  etiquetasCatalogo: PropTypes.arrayOf(PropTypes.object),
  onChange: PropTypes.func.isRequired,
  onAddEtiqueta: PropTypes.func.isRequired,
  onRemoveEtiqueta: PropTypes.func.isRequired,
};

TicketProps.defaultProps = {
  usuarios: [],
  estados: [],
  grupos: [],
  temas: [],
  etiquetasCatalogo: [],
};

export default TicketProps;
